import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { getPersonContactCardFormGroupFormArrayDataAsPersonProjectArray } from '@app/helpers/form.helper';
import {
  ProjectSetupDRequest,
  ProjectSetupDResponse,
} from '@app/models/project-setup.models';
import {
  FinancialAsset,
  PersonSimpleWithRole,
} from '@app/models/project.models';
import { LocalizedValue } from '@app/models/shared.models';
import {
  FinancialAssetConnectionToProjectType,
  FinancialAssetType,
} from '@app/models/shared.enums';
import {
  FinancialAssetFormData,
  FinancialAssetFormGroup,
  getFinancialAssetFormDataFromFinancialAsset,
  getFinancialAssetFormGroup,
} from '../../device-card/device-card-form.helper';
import {
  PersonContactCardFormGroup,
  getPersonContactCardFormGroup,
  getPersonContactCardFormGroupFromPersonSimpleWithRole,
} from '../../person-contact-card/person-contact-form/person-contact-card-form.helper';

export interface SectionDFormGroup {
  totalAmount: FormControl<number | null>;
  totalAmountType: FormControl<number | null>;
  bookingPrice: FormControl<number | null>;
  overBookingAcceptence: FormControl<boolean>;
  overbookingType: FormControl<number | null>;
  guarantorIsLegalPerson: FormControl<boolean>;
  covered: FormControl<boolean>;
  guarantors: FormArray<FormGroup<PersonContactCardFormGroup>>;
  guarantorInfo: FormControl<string>;
  transferableSecurities: FormControl<boolean>;
  transferableSecuritiesDate: FormControl<Date>;
  devices: FormArray<FormGroup<FinancialAssetFormGroup>>;
}

export function getSectionDFormGroup(
  fb: FormBuilder
): FormGroup<SectionDFormGroup> {
  return fb.nonNullable.group({
    totalAmount: fb.nonNullable.control<number | null>(null),
    totalAmountType: fb.nonNullable.control<number | null>(null),
    bookingPrice: fb.nonNullable.control<number | null>(null),
    overBookingAcceptence: fb.nonNullable.control<boolean>(false),
    overbookingType: fb.nonNullable.control<number | null>(null),
    guarantorIsLegalPerson: fb.nonNullable.control<boolean>(false),
    covered: fb.nonNullable.control<boolean>(false),
    guarantors: fb.nonNullable.array<FormGroup<PersonContactCardFormGroup>>([
      getPersonContactCardFormGroup(fb, false, 'KeszfizetoKezessegVallalo'),
    ]),
    guarantorInfo: fb.nonNullable.control<string>(''),
    transferableSecurities: fb.nonNullable.control<boolean>(false),
    transferableSecuritiesDate: fb.nonNullable.control<Date>(new Date()),
    devices: fb.nonNullable.array<FormGroup<FinancialAssetFormGroup>>([
      getFinancialAssetFormGroup(
        fb,
        FinancialAssetType.Bond,
        FinancialAssetConnectionToProjectType.ForNonOwnershipAssets
      ),
    ]),
  });
}

export function getSectionDUpdateData(
  formGroup: FormGroup<SectionDFormGroup>
): ProjectSetupDRequest {
  const date = formGroup.controls.transferableSecuritiesDate.value;
  return {
    securitiesTotalOffer: formGroup.controls.totalAmount.value!,
    securitiesType: formGroup.controls.totalAmountType.value!,
    securitieBookingPrice: formGroup.controls.bookingPrice.value!,
    oversubscriptionAcceptance: formGroup.controls.overBookingAcceptence.value,
    oversubscriptionType: formGroup.controls.overbookingType.value!,
    isGuarantorLegalEntity: formGroup.controls.guarantorIsLegalPerson.value,
    isInvestmentSecuredByThirdParty: formGroup.controls.covered.value,
    isRepurchaseCommitment: formGroup.controls.transferableSecurities.value,
    repurchaseCommitmentAndDeadline: date
      ? new Date(date).toISOString()
      : '',
    guaranteeOrSecurityInfo: formGroup.controls.guarantorInfo.value,
    persons: getPersonContactCardFormGroupFormArrayDataAsPersonProjectArray(
      formGroup.controls.guarantors
    ),
  };
}

function getGuarantorsFromPersonSimpleWithRole(
  fb: FormBuilder,
  persons: PersonSimpleWithRole[] | null,
  nameTitles?: readonly LocalizedValue[],
): FormArray<FormGroup<PersonContactCardFormGroup>> {
  const guarantors: FormArray<FormGroup<PersonContactCardFormGroup>> = fb.array<
    FormGroup<PersonContactCardFormGroup>
  >([]);
  if (persons !== null && persons.length > 0) {
    persons.forEach((person) =>
      guarantors.push(
        getPersonContactCardFormGroupFromPersonSimpleWithRole(
          fb,
          false,
          person,
          nameTitles,
        ),
      )
    );
  } else {
    guarantors.push(
      getPersonContactCardFormGroup(fb, false, 'KeszfizetoKezessegVallalo')
    );
  }
  return guarantors;
}

function getDevicesFromFinancialAsset(
  fb: FormBuilder,
  financialAssets: FinancialAsset[] | null
): FinancialAssetFormData[] {
  const devices: FinancialAssetFormData[] = [];
  if (financialAssets !== null && financialAssets.length > 0) {
    financialAssets.forEach((financialAssets) =>
      devices.push(
        getFinancialAssetFormDataFromFinancialAsset(
          financialAssets,
          FinancialAssetType.Bond,
          FinancialAssetConnectionToProjectType.ForNonOwnershipAssets
        )
      )
    );
  } else {
    devices.push(
      getFinancialAssetFormDataFromFinancialAsset(
        null,
        FinancialAssetType.Bond,
        FinancialAssetConnectionToProjectType.ForNonOwnershipAssets
      )
    );
  }
  return devices;
}

export function patchSectionDForm(
  formGroup: FormGroup<SectionDFormGroup>,
  data: ProjectSetupDResponse,
  fb: FormBuilder,
  nameTitles?: readonly LocalizedValue[],
): void {
  const repurchaseDate = data.repurchaseCommitmentAndDeadline
    ? new Date(
        typeof data.repurchaseCommitmentAndDeadline === 'string'
          ? data.repurchaseCommitmentAndDeadline
          : (data.repurchaseCommitmentAndDeadline as Date)
      )
    : new Date();

  formGroup.controls.guarantors.clear();
  getGuarantorsFromPersonSimpleWithRole(fb, data.persons, nameTitles).controls.forEach(
    (cg) => formGroup.controls.guarantors.push(cg)
  );

  formGroup.controls.devices.clear();
  const deviceData = getDevicesFromFinancialAsset(fb, data.financialAssets);
  deviceData.forEach((device) => {
    const fg = getFinancialAssetFormGroup(
      fb,
      device.type,
      device.projectConnectionType
    );
    fg.patchValue({
      nominalInterestRate: device.nominalInterestRate,
      startOfPayingInterest: device.startOfPayingInterest,
      interestPaymentFrequenty: device.interestPaymentFrequenty,
      dateOfExpiration: device.dateOfExpiration,
      applicableYield: device.applicableYield,
    });
    formGroup.controls.devices.push(fg);
  });
  formGroup.patchValue({
    totalAmount: data.securitiesTotalOffer,
    totalAmountType: data.securitiesType,
    bookingPrice: data.securitieBookingPrice,
    overBookingAcceptence: data.oversubscriptionAcceptance,
    overbookingType: data.oversubscriptionType,
    guarantorIsLegalPerson: data.isGuarantorLegalEntity,
    covered: data.isInvestmentSecuredByThirdParty,
    transferableSecurities: data.isRepurchaseCommitment,
    transferableSecuritiesDate: repurchaseDate,
    guarantorInfo: data.guaranteeOrSecurityInfo,
  });
}
